import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal, ModalBody } from 'reactstrap';
import { getPromptVersions, revertPrompt } from '../../helper/backend-methods';
import { showToast } from '../../components/Toast';
import '../../assets/css/--component-rubric.css';

/**
 * What this prompt said before, and a way back to it.
 *
 * A safety net, not an audit log: the last twenty, and the question it answers
 * is "what did it say on Monday", never "who changed what in March".
 *
 * Reverting writes a version of its own, so going back is itself undoable —
 * which is what makes it safe to press.
 */

/** How a version came about, in the customer's words. */
const SOURCES = {
  manual: 'You edited it',
  website: 'Written from your website',
  template: 'From a template',
  'quick-start': 'From quick start',
  'ai-writer': 'Built from the questions',
  'ai-edit': 'You asked for a change',
  'call-tuning': 'Suggested by real calls',
  revert: 'You put an earlier version back',
};

/** "today", "Monday", or a date — the way people say it. */
const when = (iso) => {
  if (!iso) return '';
  const then = new Date(iso);
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  const time = then.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (days === 0) return `today, ${time}`;
  if (days === 1) return `yesterday, ${time}`;
  if (days < 7) return `${then.toLocaleDateString([], { weekday: 'long' })}, ${time}`;
  return then.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
};

const VersionHistory = ({ promptId, currentPrompt, onReverted }) => {
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState(null);
  const [selected, setSelected] = useState(null);
  const [reverting, setReverting] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await getPromptVersions(promptId);
      const data = response?.data?.data ?? response?.data;
      setVersions(Array.isArray(data) ? data : []);
    } catch {
      setVersions([]);
    }
  }, [promptId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const putBack = async () => {
    setReverting(true);
    try {
      const response = await revertPrompt(promptId, selected._id);
      const data = response?.data?.data ?? response?.data;
      if (!response?.status) {
        showToast('error', 'Could not put it back', data?.message || 'Please try again.');
        return;
      }
      showToast('success', 'Put back', 'This is a version too, so you can undo it.');
      setOpen(false);
      setSelected(null);
      if (onReverted) onReverted(data);
    } catch {
      showToast('error', 'Could not put it back', 'Please try again.');
    } finally {
      setReverting(false);
    }
  };

  return (
    <>
      <Button type="button" color="link" className="p-0 fs-14" onClick={() => setOpen(true)}>
        Earlier versions
      </Button>

      <Modal isOpen={open} toggle={() => setOpen(false)} centered size="lg" scrollable>
        <ModalBody className="p-4">
          <h5 className="mb-1">Earlier versions</h5>
          <p className="fs-14 text-muted">The last twenty. This is a way back, not a record of everything.</p>

          {versions === null && <p className="fs-14 text-muted">Loading…</p>}

          {versions?.length === 0 && (
            // Every prompt that existed before this shipped starts here. Saying
            // so avoids a support ticket about missing history.
            <p className="fs-14 text-muted">
              No earlier versions yet. We keep the last twenty from now on.
            </p>
          )}

          {versions?.map((version) => (
            <div
              key={version._id}
              className={`prompt-version ${selected?._id === version._id ? 'prompt-version-selected' : ''}`}
              onClick={() => setSelected(version)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setSelected(version)}
            >
              <div className="d-flex justify-content-between align-items-center">
                <strong>{when(version.createdAt)}</strong>
                {version.complete === false && (
                  <span className="prompt-version-flag">was missing a section</span>
                )}
              </div>
              <p className="prompt-version-source">
                {SOURCES[version.source] || version.source}
                {version.instruction ? ` — “${version.instruction}”` : ''}
              </p>
            </div>
          ))}

          {selected && (
            <div className="prompt-version-preview">
              <p className="fs-13 text-muted mb-1">What it said then</p>
              <pre className="prompt-version-text">{selected.prompt}</pre>
              {currentPrompt && selected.prompt !== currentPrompt && (
                <p className="prompt-version-warn">
                  Putting this back undoes everything written since. You can undo that too.
                </p>
              )}
            </div>
          )}

          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button color="light" onClick={() => setOpen(false)}>Close</Button>
            <Button color="primary" disabled={!selected || reverting} onClick={putBack}>
              {reverting ? 'Putting it back…' : 'Put this version back'}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default VersionHistory;
